import { useRef, useState } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { useConditionalEffect, usePrevious } from 'react-delta';
import Highcharts, { ChartSelectionContextObject, PointerEventObject } from 'highcharts';
import history from 'service/customHistory';
import * as DeepMerge from 'deepmerge';

import { addTermToFilter } from 'common/util/filter/filter';
import { getLinkForRoute } from 'common/util/location';
import { quote } from 'common/util/string';
import * as K from 'common/constants';
import { useSetSearchParams } from 'hooks/useSetSearchParams';

import { UrlParams } from 'common/types';
import {
  draftSearchParamState, eventParamsState,
  queryTimeRangeState,
} from 'components/common/Managers/RouteStateManager.state';

import { getBaseGraphConfig } from 'components/common/Graphs/graphConfigs';

import { DashboardJSONType, GraphConfigType } from 'components/dashboards/types';
import { DashboardPlotData, getType } from 'components/dashboards/dashboardData';
import { calcTootipDecimals, elliptifyLabelText } from 'common/util/graph';
import { NavPlotToSearch } from 'components/dashboards/DashboardPage/modals/PlotZoom';
import ScalyrHighcharts from 'components/common/ScalyrHighcharts';
import { highchartsStylesState, plotsStylesState } from 'styles/stylesProvider';

import './DashboardHighcharts.scss';
import { isNumeric } from 'common/functions';

const mapChartType = (vizType: DashboardJSONType): string => {
  if (vizType.isBar) {
    return 'column';
  }

  if (vizType.isPie) {
    return 'pie';
  }

  if (vizType.isSmoothed) {
    return vizType.isLine ? 'spline' : 'areaspline';
  }
  return vizType.isLine ? 'line' : 'area';
};

const MAX_LABEL_LEN = 50;

export const calcBarWidth = (chartDiv: HTMLElement, series: Highcharts.Point[]): number => Math.floor(chartDiv.offsetWidth / (K.DEFAULT_COLUMN_SPACING * series.length));

export const recalcBarWidths = (chart: Highcharts.Chart): void => {
  chart.series.forEach((series) => {
    if (series.type === 'column') {
      const pointWidth = calcBarWidth(chart.container, series.data);
      const options = series.options as Highcharts.SeriesColumnOptions;
      if (pointWidth !== options.pointWidth) {
        const newSeriesOptions = { ...series.options, pointWidth };
        series.update(newSeriesOptions as unknown as Highcharts.SeriesOptionsType);
      }
    }
  });
};

export interface DashboardHighchartsType {
  index: number;
  config: GraphConfigType;
  plots: DashboardPlotData[];
  windowWidth: number;
  warning?: string;
  highchartsOptions?: Highcharts.Options;
  chartInstanceCallback: (index: number, newChart: Highcharts.Chart) => void;
  exploreHref?: string;
}

/**
 * Line, bar, area pr pie chart chart display for a dashboard.
 *
 * @prop index                  The index of this chart within the dashbaord config
 * @prop config                 The config stanza for just this chart
 * @prop plots                  The process list of plots for this chart
 * @prop isDarkMode             true if dark mode, false otherwise
 * @prop windowWidth            The overall window width
 * @prop warning                Any warning returned from the backend
 * @prop highchartsOptions      Any extra Highcharts config to layer over this one
 * @prop chartInstanceCallback  A callback to capture the Highcharts chart instance -- used for resizing
 * @prop exploreHref            a href string to use as the link when a user clicks on the chart
 */
const DashboardHighcharts = ({
  index,
  config,
  plots,
  windowWidth,
  warning,
  highchartsOptions,
  chartInstanceCallback,
  exploreHref,
}: DashboardHighchartsType): JSX.Element => {

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const setSearchParams = useSetSearchParams();
  const chartRef = useRef<HTMLDivElement>(null);
  const [chart, setChart] = useState<Highcharts.Chart | null>(null);
  const [navToSearch, setNavToSearch] = useState(false);
  const highchartsStyles = useRecoilValue(highchartsStylesState);
  const plotsStyles = useRecoilValue(plotsStylesState);
  const vizType = getType(config);
  const eventParams = useRecoilValue(eventParamsState);

  // --------------------------------------------------------------------------------------------------------------- RESIZE

  useConditionalEffect(() => {
    if (chart) {
      recalcBarWidths(chart);
    }
  }, windowWidth !== usePrevious(windowWidth));

  // --------------------------------------------------------------------------------------------------------------- PLOTS
  // Scalyr's preferred date/time format
  const timeFormat = '%a %b %e, %l:%M:%S %p';

  const series: Array<Highcharts.SeriesOptionsType> = [];
  const chartType = mapChartType(config);

  let maxValue = Number.MIN_SAFE_INTEGER;
  plots.forEach((plot) => {
    const seriesEntry = {
      name: elliptifyLabelText(plot.label || 'Untitled', MAX_LABEL_LEN, true),
      color: plot.color,
      data: plot.seriesData,
      innerSize: '', // freakin typescript -- can't use below unless it's here
      marker: {
        symbol: 'circle',
      },
      pointPadding: 0.05,
      groupPadding: 0,
      borderWidth: 0,
      pointWidth: chartRef?.current ? calcBarWidth(chartRef?.current, plot.seriesData as unknown as Highcharts.Point[]) : 0,
      type: mapChartType(vizType),
    };

    if (chartType === 'pie' || chartType === 'donut') {
      seriesEntry.borderWidth = 1;
    }

    if (config.graphStyle === 'donut') {
      seriesEntry.innerSize = '60%';
    }

    series.push(seriesEntry as Highcharts.SeriesOptionsType);
    maxValue = Math.max(maxValue, plot.maxValue || Number.MIN_SAFE_INTEGER);
  });

  const valueDecimals = calcTootipDecimals(maxValue);

  const tooltip = {
    outside: true,
    hideDelay: 0,
    useHTML: true,
    followPointer: true,
    enabled: true,
    crosshairs: true,
    xDateFormat: timeFormat,
    borderColor: 'transparent',
    headerFormat: `
      <table class="DashboardHighcharts__Tooltip">
        <tbody>
      `,
    pointFormat: `
      <tr>
        <td style="color: {series.color}" class="DashboardHighcharts__TooltipPlotName">{series.name} </td>
        <td class="DashboardHighcharts__TooltipValue">{point.y}</td>
      </tr>
      `,
    footerFormat: `
      <tr>
        <td colspan="3" class="DashboardHighcharts__TooltipFooter">
          <div class="DashboardHighcharts__TooltipDateTime">{point.key}</div>
        </td>
      </tr></tbody></table>
      `,
    valueDecimals,
    shared: true,
  };

  // --------------------------------------------------------------------------------------------------------------- PIE / DONUT TOOLTIP
  if (chartType === 'pie' || chartType === 'donut') {
    tooltip.useHTML = true;
    tooltip.pointFormat = '';
    tooltip.footerFormat = '{point.key}: <b>{point.y}</b>';
  }

  // --------------------------------------------------------------------------------------------------------------- CHART OPTIONS
  const pieSliceLink = useRecoilCallback(({ snapshot }) => (field: string | undefined, value: any): string => {
    const draftParams = snapshot.getLoadable(draftSearchParamState).contents as UrlParams;
    const newFilter = addTermToFilter({
      filter: draftParams[K.URL_PARAM.FILTER] || '',
      field:  field || '',
      op: '=',
      value:  value != null ? quote(value) : '',
    });
    const params = { ...draftParams, [K.URL_PARAM.FILTER]: newFilter } as UrlParams;
    return getLinkForRoute(K.ROUTE.DASHBOARD, eventParams, params);
  });

  const dashboardGraphConfig = {
    chart: {
      animation: false,
      type: chartType,
      zoomType: 'x',
      events: {
        // Capture the mouse selection event to update timerange
        selection(e: ChartSelectionContextObject): void {
          e.preventDefault();
          setSearchParams({
            [K.URL_PARAM.START_TIME]: Math.floor(e.xAxis[0].min),
            [K.URL_PARAM.END_TIME]: Math.floor(e.xAxis[0].max),
          } as unknown as UrlParams);
        },
        click(e: PointerEventObject): void {
          if (chartType !== 'pie' && chartType !== 'donut') {
            e.preventDefault();
            setNavToSearch(true);
          }
        },
      },
      alignTicks: false,
      style: {
        fontFamily: 'Open Sans',
      },
      marginTop: 10,
      marginBottom: 35,
      spacingTop: 10,
      spacingBottom: 15,
      spacingLeft: 10,
      spacingRight: 10,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        colors: plotsStyles,
        size: '100%',
        dataLabels: {
          enabled: true,
          distance: 20,
          format: '<b>{point.name}</b>:<br/>{point.percentage:.1f}%',
          style: {
            fontWeight: 'normal',
            fontSize: 12,
          },
        },
        tooltip,
      },
      series: {
        pointPadding: 0.05,
        lineWidth: 1.25,
        connectNulls: true,
        states: {
          hover: {
            enabled: true,
            lineWidth: 1.25,
          },
        },
        marker: {
          enabled: false,
        },
        point: {
          events: {
            click(): void {
              if (vizType.isPie) {
                // @ts-expect-error
                if (this.name !== 'Other') {
                  // @ts-expect-error
                  history.push(pieSliceLink(plots[0]?.facet, this.name));
                }
              }
              if (chartType !== 'pie' && chartType !== 'donut') {
                setNavToSearch(true);
              }
            },
          },
        },
      },
    },
    xAxis: { min: parseFloat(startTime), max: parseFloat(endTime) },
    yAxis: {
      type: (config.yScale && config.yScale === 'logarithmic') ? config.yScale : 'linear',
      startOnTick: false,
      endOnTick: false,
      title: {
        text: undefined,
      },
      // https://scalyr.myjetbrains.com/youtrack/issue/FRONT-5822
      // We use tickPositioner to set ymax and ymin values if they exist
      tickPositioner(): number[] {

        // @ts-expect-error
        const positions = this.tickPositions ? [...this.tickPositions] : [];

        if (isNumeric(config.ymin) && positions.length > 0) {
          positions[0] = Number(config.ymin);
        }

        if (isNumeric(config.ymax) && positions.length > 0) {
          positions[positions.length - 1] = Number(config.ymax);
        }

        return positions;
      },
      min: isNumeric(config.ymin) ? Number(config.ymin) : null,
      max: isNumeric(config.ymax) ? Number(config.ymax) : null,
      plotBands: config.plotBands?.map((band) => ({ ...band, zIndex: 5, events: null })),
      plotLines: config.plotLines?.map((line) => ({ ...line, zIndex: 5, events: null })),
    },
    tooltip,
    series,
  };

  // --------------------------------------------------------------------------------------------------------------- PIE / DONUT OPTIONS
  if (chartType === 'pie' || chartType === 'donut') {
    dashboardGraphConfig.chart.marginTop = 5;
    dashboardGraphConfig.chart.spacingBottom = 0;
    dashboardGraphConfig.chart.spacingRight = 0;
    dashboardGraphConfig.chart.marginBottom = 30;
  }


  // --------------------------------------------------------------------------------------------------------------- CHART DISPLAY
  // Use the Scalyr defaults, then override for dashboards and possibly theme
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const configOptions = [getBaseGraphConfig(), dashboardGraphConfig, highchartsStyles];
  if (highchartsOptions) {
    configOptions.push(highchartsOptions);
  }
  const hcConfig: Highcharts.Options = DeepMerge.all(configOptions);

  // Let the parent know the Highcharts chart instance
  const afterChartCreated = (newChart: Highcharts.Chart): void => {
    setChart(newChart);
    if (chartInstanceCallback) {
      chartInstanceCallback(index, newChart);
    }
  };

  // Settings global language options
  Highcharts.setOptions(
    {
      lang: {
        decimalPoint: '.',
        thousandsSep: ',',
      },
    },
  );

  return (
    <div className="DashboardGraph__Content DashboardGraph__Content--chart DashboardHighcharts" ref={chartRef}>
      <ScalyrHighcharts
        options={hcConfig}
        highcharts={Highcharts}
        containerProps={{ style: { height: '100%', width: '100%' } }}
        callback={afterChartCreated}
      />
      {warning && <div className="DashboardGraph__Warning">{warning}</div>}

      {!vizType.isPie && <NavPlotToSearch graphConfig={config} plots={plots} doNavigate={navToSearch} onClose={(): void => setNavToSearch(false)} href={exploreHref} /> }

    </div>
  );
};

export default DashboardHighcharts;
